/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/* eslint-disable react/prop-types */
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, useState, useCallback, useEffect, useMemo } from "react"
import Cropper from "react-easy-crop"
import { Point, Area } from "react-easy-crop/types"
import {
  Drawer,
  Row,
  Col,
  Upload,
  Button,
  Alert,
  Spin,
  Flex,
  Grid,
} from "@duxton-web/ui"
import {
  ChevronLeftUniversal,
  CloseUniversal,
  Rotate180Universal,
} from "@duxton-web/icons"
import GrabMexProImageEditor, {
  ImageEditorCallbacks,
  GrabMexI18nConfig,
  RemoveBgApiResult,
  AutoEnhanceResult,
  OptimalImageAdjustments,
  SaveOrCancelData,
} from "@mobile/grab-pro-image-editor-react"
import { omit } from "lodash"
import { saveFile } from "utils/misc"
import {
  getTransformedImageDataURI,
  readFileAsDataURL,
  getFileMimeType,
  isFileValidImageSize,
  convertUrlToBase64DataUrl,
  getImageMimeType,
  getBase64String,
} from "utils/photo_helper"
import { hasEcommerceStore } from "utils/user_helper"
import { isWebview } from "utils/webview"
import { useTranslationsMap } from "common/utils/i18n"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { EnhanceImageTask } from "store/duck/menu.type"
import { FoodmaxapipbEnhanceTask } from "generated/foodmaxapi.type"
import {
  getResponseErrorEventLabels,
  getResponseErrorEventLabelsForRTK,
  TroyWebTracker,
} from "utils/tracking"
import {
  EventAction,
  EventCategory,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { AxiosAction } from "types/api"
import { UploadItemPhotoResponse } from "types/api/menuUploadItemPhoto.type"
import { AnyAction } from "redux"
import { datadogRum } from "@datadog/browser-rum"
import {
  useEnhanceImageMutation,
  useUploadItemPhotoMutation,
} from "queries/menuApi"
import {
  backdropFilter,
  drawerFooter,
  photoUploadDrawer,
  uploadPhotoErrorStyle,
  uploadPhotoWarningStyle,
} from "./styles"

const Dragger = Upload.Dragger

export interface PhotoUploadDrawerProps {
  visible: boolean
  uploadedImg: string
  photoUploadTitle: string
  onTogglePhotoUploaderVisible: (val: boolean) => void
  onSubmitUploadPhoto: (photoUrl: string) => void
  eventCategory: EventCategory
  /** Override merchantID (e.g. storeID from ecommerce context when menu state has no merchantID) */
  merchantID?: string
  /** Override merchantGroupID when used outside menu context */
  merchantGroupID?: string
  // kept for legacy purposes - if passed in, use old flow where validation errors are NOT shown. see config.enableMultipleItemPhoto
  validationHandlingOverride?: {
    onError: (errorMessage?: string) => void
    onWarn: (warnMessage?: string) => void
    error?: string
    warning?: string
  }
  allowExistingPhotoEdit?: boolean
  /**
   * Optional external upload function.
   * When provided it replaces the internal Redux-dispatch upload so callers can
   * use RTK Query (or any other mechanism) for the actual HTTP upload.
   * Receives the full data-URL (e.g. "data:image/jpeg;base64,...") and must
   * resolve with the CDN URL returned by the upload endpoint.
   */
  uploadFn?: (dataUrl: string) => Promise<string>
  /**
   * View-only mode for ecommerce inventory-only users: shows the full image,
   * hides the drawer title, disables all action buttons except Download.
   */
  readOnly?: boolean
}

const initialCrop: Point = { x: 0, y: 0 }
const initialRotation = 0
const initialZoom = 1

export const PhotoUploadDrawer: FC<PhotoUploadDrawerProps> = ({
  visible,
  uploadedImg,
  photoUploadTitle,
  onTogglePhotoUploaderVisible,
  onSubmitUploadPhoto,
  eventCategory,
  merchantID: merchantIDProp,
  merchantGroupID: merchantGroupIDProp,
  validationHandlingOverride,
  allowExistingPhotoEdit,
  uploadFn,
  readOnly = false,
}) => {
  // Below md the 480px drawer overflows phone viewports; go full-width there
  // and stack the action buttons instead of forcing them onto one line.
  const { md } = Grid.useBreakpoint()
  const {
    merchantID: merchantIDFromState,
    drawer: { item, showUploadPhoto },
    isMartStore,
    merchantGroupID: merchantGroupIDFromState,
    menuGroupDetail: { menuGroupID },
    enableGrabMexProImageEditor,
  } = useSelector((state: AppState) => state?.menu)

  const merchantID = merchantIDProp ?? merchantIDFromState
  const merchantGroupID = merchantGroupIDProp ?? merchantGroupIDFromState

  const isEcommerceStore = hasEcommerceStore()

  const textMap = useTranslationsMap({
    dragAndDropText: "menu.item.modal.photo.dragAndDrop",
    uploadFromCompText: "menu.item.modal.photo.uploadFromComputer",
    photoGuideLineText: "menu.item.modal.photo.photGuideLines",
    itemCenteredText:
      isMartStore || isEcommerceStore
        ? "catalogue.item.modal.photo.itemCentered"
        : "menu.item.modal.photo.dishCentered",
    blurryText:
      isMartStore || isEcommerceStore
        ? "catalogue.item.modal.photo.blurry"
        : "menu.item.modal.photo.blurry",
    upto2mb: "menu.item.modal.photo.upTo2mb",
    addPhotoText: "menu.item.modal.photo.addPhoto",
    rotatePhotoText: "menu.item.modal.photo.rotate",
    deletePhotoText: "menu.item.modal.photo.delete",
    exceed2mb: "menu.item.modal.exceed2mb",
    downloadPhoto: "menu.item.button.downloadPhoto",
    selectAnotherPhoto: "menu.item.modal.photo.selectAnotherPhoto",
    uploading: "menu.item.modal.photo.uploading",
    imageProcessingError: "menu.item.modal.photo.imageProcessingError",
    invalidImageType: "menu.item.modal.photo.invalidImageType",
    generalError: "common.error",
  })

  const photoEnhancerTextMap = useTranslationsMap({
    autoEnhancer: "photoEnhancer.button.autoEnhancer",
    crop: "photoEnhancer.label.crop",
    adjust: "photoEnhancer.label.adjust",
    erase: "photoEnhancer.label.erase",
    seeOriginal: "photoEnhancer.button.seeOriginal",
    optimizingImage: "photoEnhancer.body.optimizingImage",
    reverse: "photoEnhancer.button.reverse",
    rotate: "photoEnhancer.button.rotate",
    brightness: "photoEnhancer.label.brightness",
    contrast: "photoEnhancer.label.contrast",
    removeBackground: "photoEnhancer.label.removeBackground",
    removeObject: "photoEnhancer.label.removeObject",
    enhancementInProgress: "photoEnhancer.body.enhancementInProgress",
    cancel: "common.cancel",
  })

  const isItemEdit = !!item?.itemID
  const [crop, setCrop] = useState(initialCrop)
  const [rotation, setRotation] = useState(initialRotation)
  const [zoom, setZoom] = useState(initialZoom)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>()
  const [localUploadedImg, setLocalUploadedImg] = useState<string>("")
  const [downloadPhotoUrl, setDownloadPhotoUrl] = useState<string>("")
  const [isEditProEditor, setIsEditProEditor] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [hasBeenUploaded, setHasBeenUploaded] = useState(false)
  const [uploadImgProgress, setUploadImgProgress] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [uploadPhotoError, setUploadPhotoError] = useState<string>()
  const [uploadPhotoWarning, setUploadPhotoWarning] = useState<string>()
  const [proEditorHasError, setProEditorHasError] = useState(false)
  const [
    hasAttemptedCropperBase64Fallback,
    setHasAttemptedCropperBase64Fallback,
  ] = useState(false)
  const dispatch = useDispatch()
  const [enhanceImageRtk] = useEnhanceImageMutation()
  const {
    flags: {
      enhanceImage: isRtkEnhanceImageEnabled,
      uploadItemPhoto: isRtkUploadItemPhotoEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const photoIssues = useTranslationsMap({
    Non_Food: "menu.item.modal.nonFoodPhoto",
    Dish_Blur: "menu.item.modal.blurredPhoto",
    Jericho_Watermark: "menu.item.modal.watermark",
  })

  const [uploadItemPhotoRtk] = useUploadItemPhotoMutation()

  const itemID = item?.itemID ?? ""
  const trackingLabels = useMemo(
    () => ({
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      ITEM_ID: itemID,
      INTENT: isItemEdit ? "EDIT_ITEM" : "ADD_ITEM",
    }),
    [merchantID, merchantGroupID, menuGroupID, itemID, isItemEdit],
  )

  const shouldUseProEditor = !!enableGrabMexProImageEditor && !proEditorHasError

  useEffect(() => {
    // this effect triggered when uploadedImg or
    // visible changed
    // Update localUploadedImg when uploadedImg
    // from parent is updated and drawer is open
    let isCancelled = false

    if (uploadedImg !== localUploadedImg && visible) {
      if (readOnly) {
        // View-only: show the image directly — no base64 conversion or pro-editor.
        setLocalUploadedImg(uploadedImg)
        setDownloadPhotoUrl(uploadedImg)
        setIsEdit(true)
        setHasAttemptedCropperBase64Fallback(false)
      } else {
        // A raw data URL means the parent is feeding the un-cropped original;
        // skip the read-only "already-uploaded" view so the cropper is enabled.
        const nextIsEdit = !uploadedImg.startsWith("data:")

        if (enableGrabMexProImageEditor || allowExistingPhotoEdit) {
          setIsLoading(true)
          convertUrlToBase64DataUrl(uploadedImg)
            .then(base64DataUrl => {
              if (isCancelled) return
              setLocalUploadedImg(base64DataUrl)
              setDownloadPhotoUrl(uploadedImg)
              setIsEdit(nextIsEdit)
              setIsEditProEditor(!!enableGrabMexProImageEditor)
              setHasAttemptedCropperBase64Fallback(false)
            })
            .catch(error => {
              if (isCancelled) return

              // Fall back to old cropper feature
              setProEditorHasError(true)
              setLocalUploadedImg(uploadedImg)
              setDownloadPhotoUrl(uploadedImg)
              setIsEdit(nextIsEdit)
              setHasAttemptedCropperBase64Fallback(false)
              datadogRum.addError(error, {
                tags: { context: "PhotoUploadDrawerProEditor" },
              })
            })
            .finally(() => {
              if (isCancelled) return
              setIsLoading(false)
            })
        } else {
          setLocalUploadedImg(uploadedImg)
          setDownloadPhotoUrl(uploadedImg)
          setIsEdit(nextIsEdit)
          setHasAttemptedCropperBase64Fallback(false)
        }
      }
    }

    return () => {
      isCancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImg, visible])

  useEffect(() => {
    if (visible) {
      setProEditorHasError(false)
    }
  }, [visible])

  useEffect(() => {
    if (validationHandlingOverride) {
      const { error, warning } = validationHandlingOverride
      setUploadPhotoWarning(warning)
      setUploadPhotoError(error)
    }
  }, [validationHandlingOverride])

  const handleCleanupPhoto = useCallback(() => {
    setIsEdit(false)
    setLocalUploadedImg("")
    setDownloadPhotoUrl("")
    setIsEditProEditor(false)
    setUploadPhotoError(undefined)
    setUploadPhotoWarning(undefined)
    setHasBeenUploaded(false)
    setRotation(initialRotation)
    setCrop(initialCrop)
    setZoom(initialZoom)
    setCroppedAreaPixels(undefined)
    setUploadImgProgress(false)
    setIsLoading(false)
    setHasAttemptedCropperBase64Fallback(false)
  }, [])

  const handleCropperMediaError = useCallback(async () => {
    if (hasAttemptedCropperBase64Fallback) return

    setHasAttemptedCropperBase64Fallback(true)
    try {
      const base64DataUrl = await convertUrlToBase64DataUrl(localUploadedImg)
      setLocalUploadedImg(base64DataUrl)
    } catch (error) {
      datadogRum.addError(error, {
        tags: { context: "PhotoUploadDrawerCropperMediaError" },
      })
    }
  }, [hasAttemptedCropperBase64Fallback, localUploadedImg])

  const handleOnClosePhotoUploadDrawer = useCallback(() => {
    onTogglePhotoUploaderVisible(false)
  }, [onTogglePhotoUploaderVisible])

  const onCropComplete = useCallback(
    (croppedArea: Area, _croppedAreaPixels: Area) => {
      setCroppedAreaPixels(_croppedAreaPixels)
    },
    [],
  )

  const uploadPhoto = useCallback(
    async (photoDataUrl: string): Promise<string | undefined> => {
      setUploadImgProgress(true)
      setUploadPhotoError(undefined)
      setUploadPhotoWarning(undefined)

      // When an external upload function is provided, use it instead of the
      // internal Redux dispatch.  This allows callers (e.g. the ecommerce
      // product form) to use RTK Query for the actual HTTP upload while still
      // benefiting from the crop/validate UI here.
      if (uploadFn) {
        try {
          const cdnUrl = await uploadFn(photoDataUrl)
          setLocalUploadedImg(cdnUrl)
          setDownloadPhotoUrl(cdnUrl)
          setHasBeenUploaded(true)
          setUploadImgProgress(false)
          return cdnUrl
        } catch {
          setUploadPhotoError(textMap.generalError)
          setUploadImgProgress(false)
          return undefined
        }
      }

      let uploadedPhotoUrlResult: string | undefined
      let uploadPhotoSuccess = false
      let labels: any = { ...trackingLabels }
      let uploadErrorTxt = ""
      let uploadWarningTxt = ""
      let isFatalError = false

      // The RTK Query and legacy Redux paths resolve to the same upload-file
      // response; apply its photo-detection outcome to the shared upload state
      // and tracking labels.
      const applyDetectionResult = (dataPayload?: UploadItemPhotoResponse) => {
        uploadedPhotoUrlResult = dataPayload?.url
        const { errorItems, warningItems } =
          dataPayload?.metadata?.detection ?? {}
        if (errorItems?.length) {
          errorItems.forEach(e => {
            uploadErrorTxt += `${photoIssues[e] ?? e}, `
          })
          uploadErrorTxt = uploadErrorTxt.replace(/, $/, "")
          setUploadPhotoError(uploadErrorTxt)
          labels = {
            ...labels,
            STATUS: "FAILURE",
            REASON: errorItems.join(", "),
          }
        } else if (warningItems?.length) {
          warningItems.forEach(w => {
            uploadWarningTxt += `${photoIssues[w] ?? w}, `
          })
          uploadWarningTxt = uploadWarningTxt.replace(/, $/, "")
          setUploadPhotoWarning(uploadWarningTxt)
          labels = {
            ...labels,
            STATUS: "SUCCESS",
            REASON: warningItems.join(", "),
          }
        } else {
          uploadPhotoSuccess = true
          labels = {
            ...labels,
            STATUS: "SUCCESS",
          }
        }
      }

      // Both upload paths emit the same submit + RTK-migration event pair; only
      // the source of the upload differs.
      const sendUploadTrackingEvents = (fromRtkQuery: boolean) => {
        TroyWebTracker.sendEvent({
          category: eventCategory,
          action: EventAction.submitAddPhoto,
          labels: {
            ...labels,
            TRAFFIC_MEDIUM: showUploadPhoto ? "MISSING_PHOTO" : "",
            IMAGE_EDITOR: shouldUseProEditor ? "GRAB_MEX_PRO_IMAGE_EDITOR" : "",
          },
        })
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.uploadItemPhoto,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkUploadItemPhotoEnabled,
            FROM_RTK_QUERY: fromRtkQuery,
            FROM_OLD_REDUX: !fromRtkQuery,
            ACTION_NAME: "uploadItemPhoto",
          },
        })
      }

      if (!isRtkFlagLoading && isRtkUploadItemPhotoEnabled) {
        const result = await uploadItemPhotoRtk({
          merchantID,
          merchantGroupID,
          photo: photoDataUrl.split(",")[1],
          type: photoDataUrl.split(";")[0].split("/")[1],
          menuGroupID,
        })

        if ("error" in result) {
          setUploadImgProgress(false)
          isFatalError = true
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabelsForRTK(result),
          }
        } else if (result.data) {
          applyDetectionResult(result.data)
        }

        sendUploadTrackingEvents(true)
        // While migration flags are loading, do not fall back to old Redux: the enabled bit is
        // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
        // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
        // the trigger UI while loading so clicks are not silently dropped.
      } else if (!isRtkFlagLoading) {
        const resp = (await dispatch(
          actionCreator.menu.uploadItemPhoto(
            photoDataUrl.split(",")[1],
            photoDataUrl.split(";")[0].split("/")[1],
            merchantID,
            merchantGroupID,
            menuGroupID,
          ) as AnyAction,
        )) as AxiosAction<UploadItemPhotoResponse>

        if (resp.type.endsWith("_SUCCESS")) {
          applyDetectionResult(resp?.payload?.data)
        } else if (resp?.type?.endsWith("_FAIL")) {
          setUploadImgProgress(false)
          isFatalError = true
          labels = {
            ...labels,
            STATUS: "FAILURE",
            ...getResponseErrorEventLabels(resp),
          }
        }

        sendUploadTrackingEvents(false)
      } else {
        setUploadImgProgress(false)
        return undefined
      }

      if (uploadedPhotoUrlResult) {
        setLocalUploadedImg(uploadedPhotoUrlResult)
        setDownloadPhotoUrl(uploadedPhotoUrlResult)
        setHasBeenUploaded(true)
      }
      if (validationHandlingOverride && !isFatalError) {
        // since we're skipping validation, we should accept the url as is and notify parent component of the error/warn
        const { onError, onWarn } = validationHandlingOverride
        if (uploadErrorTxt) {
          onError(uploadErrorTxt)
        }
        if (uploadWarningTxt) {
          onWarn(uploadWarningTxt)
        }
        return uploadedPhotoUrlResult
      }
      return uploadPhotoSuccess ? uploadedPhotoUrlResult : undefined
    },
    [
      uploadFn,
      photoIssues,
      trackingLabels,
      dispatch,
      merchantID,
      eventCategory,
      validationHandlingOverride,
      menuGroupID,
      merchantGroupID,
      showUploadPhoto,
      shouldUseProEditor,
      textMap.generalError,
      isRtkFlagLoading,
      isRtkUploadItemPhotoEnabled,
      uploadItemPhotoRtk,
    ],
  )

  const onAddPhotoSuccess = useCallback(
    (photoUrl: string) => {
      onSubmitUploadPhoto(photoUrl)
      handleOnClosePhotoUploadDrawer()
    },
    [handleOnClosePhotoUploadDrawer, onSubmitUploadPhoto],
  )

  const onAddPhotoClick = useCallback(async () => {
    if (uploadPhotoWarning) {
      // we're just confirming here that we're NOT listening to the warning if we click on the button again
      onAddPhotoSuccess(localUploadedImg)
    } else {
      let croppedImageResultUri: string
      try {
        croppedImageResultUri = await getTransformedImageDataURI(
          localUploadedImg,
          croppedAreaPixels,
          rotation,
        )
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
        // This shouldn't occur, but if it does we should track the error
        setUploadPhotoError(textMap.imageProcessingError)
        datadogRum.addError(error, {
          tags: { context: "PhotoUploadDrawer" },
        })
        return
      }

      try {
        const uploadedPhotoUrlResult = await uploadPhoto(croppedImageResultUri)
        if (uploadedPhotoUrlResult) {
          onAddPhotoSuccess(uploadedPhotoUrlResult)
        }
      } catch (e) {
        /* istanbul ignore next */
        // eslint-disable-next-line no-console
        console.error(e)
      }
    }
  }, [
    localUploadedImg,
    uploadPhotoWarning,
    onAddPhotoSuccess,
    uploadPhoto,
    croppedAreaPixels,
    rotation,
    textMap.imageProcessingError,
  ])

  const onFileChange: React.ComponentProps<
    typeof Dragger
  >["onChange"] = async e => {
    if (e.fileList && e.fileList.length > 0) {
      const file = e.fileList[0].originFileObj
      if (file) {
        const imageDataUrl: string = await readFileAsDataURL(file)
        setIsLoading(false)
        setLocalUploadedImg(imageDataUrl)
        setDownloadPhotoUrl("")
        setUploadPhotoError(undefined)
        setHasAttemptedCropperBase64Fallback(false)

        if (enableGrabMexProImageEditor && !proEditorHasError) {
          setIsEditProEditor(true)
        }
      }
    }
  }

  const handleRotatePhoto = () => {
    setRotation(prev => prev + 90)
  }

  const handleBeforeUploadPhoto: React.ComponentProps<
    typeof Dragger
  >["beforeUpload"] = async file => {
    setIsLoading(true)
    const fileMime = await getFileMimeType(file)
    if (fileMime === "unknown") {
      setUploadPhotoError(textMap.invalidImageType)
      setIsLoading(false)

      // Need to return Upload.LIST_IGNORE to prevent upload image
      return Upload.LIST_IGNORE
    }

    const isLtOrEqual2M = isFileValidImageSize(file)
    if (!isLtOrEqual2M) {
      setUploadPhotoError(textMap.exceed2mb)
      setIsLoading(false)

      // Need to return Upload.LIST_IGNORE to prevent upload image
      return Upload.LIST_IGNORE
    }

    return undefined
  }

  const handleDownloadPhoto = async () => {
    try {
      const response = await fetch(downloadPhotoUrl, { cache: "no-cache" })
      if (!response.ok) {
        throw new Error()
      }
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const fileName = new URL(downloadPhotoUrl).pathname.split("/").pop()
      saveFile(objectUrl, fileName)
      // Defer revoke so the browser has time to consume the blob URL for the
      // download. Revoking synchronously can cause empty/failed downloads in
      // some browsers (notably Safari).
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
    } catch {
      window.open(downloadPhotoUrl, "_blank")
    }
  }

  const handleEnhanceImage = useCallback(
    async (
      base64Image: string,
      task: EnhanceImageTask,
    ): Promise<RemoveBgApiResult> => {
      // While migration flags are loading, do not fall back to old Redux: the enabled bit is
      // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
      // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
      // the trigger UI while loading so clicks are not silently dropped.
      if (!isRtkFlagLoading && isRtkEnhanceImageEnabled) {
        const result = await enhanceImageRtk({
          image: base64Image,
          task,
          merchantID,
          merchantGroupID,
        })

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.enhanceImage,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkEnhanceImageEnabled,
            FROM_RTK_QUERY: true,
            FROM_OLD_REDUX: false,
            ACTION_NAME: "enhanceImage",
          },
        })

        if (result.error) {
          const errorData = result.error as { data?: { message?: string } }
          const errorMsg = errorData?.data?.message ?? textMap.generalError

          TroyWebTracker.sendEvent({
            category: eventCategory,
            action: EventAction.grabMexProImageEditorEnhanceImage,
            labels: {
              ...trackingLabels,
              TASK: task,
              STATUS: "FAILURE",
              ERROR_MESSAGE: errorMsg,
              ...getResponseErrorEventLabelsForRTK(result),
            },
          })

          return {
            errorMessage: errorMsg,
          }
        }

        const enhancedBase64Image = result.data?.image ?? ""
        const isRemoveBg =
          result.data?.processedTasks?.includes(
            FoodmaxapipbEnhanceTask.BACKGROUND_REMOVAL,
          ) ?? false

        TroyWebTracker.sendEvent({
          category: eventCategory,
          action: EventAction.grabMexProImageEditorEnhanceImage,
          labels: {
            ...trackingLabels,
            TASK: task,
            STATUS: "SUCCESS",
            DID_REMOVE_BG: isRemoveBg,
          },
        })

        return {
          base64Image: enhancedBase64Image,
          didRemoveBg: isRemoveBg,
        }
      }
      if (!isRtkFlagLoading) {
        const res = (await dispatch(
          actionCreator.menu.enhanceImage({
            image: base64Image,
            task,
            merchantID,
            merchantGroupID,
          }) as any,
        )) as AxiosAction<{ image: string; processedTasks: EnhanceImageTask[] }>

        TroyWebTracker.sendEvent({
          category: EventCategoryEng.rtkMigration,
          action: EventActionEng.enhanceImage,
          labels: {
            MERCHANT_ID: merchantID,
            IS_RTK_ENABLED: isRtkEnhanceImageEnabled,
            FROM_RTK_QUERY: false,
            FROM_OLD_REDUX: true,
            ACTION_NAME: "enhanceImage",
          },
        })

        if (res.error) {
          const errorMsg =
            res.error.response?.data?.message ?? textMap.generalError

          TroyWebTracker.sendEvent({
            category: eventCategory,
            action: EventAction.grabMexProImageEditorEnhanceImage,
            labels: {
              ...trackingLabels,
              TASK: task,
              STATUS: "FAILURE",
              ERROR_MESSAGE: errorMsg,
            },
          })

          return {
            errorMessage: errorMsg,
          }
        }

        const enhancedBase64Image = res.payload?.data?.image ?? ""
        const isRemoveBg =
          res.payload?.data?.processedTasks?.includes(
            EnhanceImageTask.BACKGROUND_REMOVAL,
          ) ?? false

        TroyWebTracker.sendEvent({
          category: eventCategory,
          action: EventAction.grabMexProImageEditorEnhanceImage,
          labels: {
            ...trackingLabels,
            TASK: task,
            STATUS: "SUCCESS",
            DID_REMOVE_BG: isRemoveBg,
          },
        })

        return {
          base64Image: enhancedBase64Image,
          didRemoveBg: isRemoveBg,
        }
      }

      return { errorMessage: textMap.generalError }
    },
    [
      dispatch,
      enhanceImageRtk,
      isRtkEnhanceImageEnabled,
      isRtkFlagLoading,
      merchantID,
      merchantGroupID,
      textMap.generalError,
      eventCategory,
      trackingLabels,
    ],
  )

  const handleProEditorImageEditingComplete = useCallback(
    async (editedBase64Image: string) => {
      setUploadImgProgress(true)

      let compressedDataUrl: string
      try {
        // Detect the actual mime type from the base64 string
        // The mime type prefix doesn't matter - getImageMimeType reads the actual file header
        const dataUrlForDetection = `data:;base64,${editedBase64Image}`
        const detectedMimeType = await getImageMimeType(dataUrlForDetection)
        const mimeType =
          detectedMimeType !== "unknown" ? detectedMimeType : "image/jpeg"
        const dataUrl = `data:${mimeType};base64,${editedBase64Image}`

        // Compress the edited image to ensure it meets size requirements
        compressedDataUrl = await getTransformedImageDataURI(dataUrl)
      } catch (error) {
        // This shouldn't occur, but if it does we should track the error
        setUploadPhotoError(textMap.imageProcessingError)
        datadogRum.addError(error, {
          tags: { context: "PhotoUploadDrawerProEditor" },
        })

        setUploadImgProgress(false)
        return
      }

      const uploadedPhotoUrlResult = await uploadPhoto(compressedDataUrl)
      if (uploadedPhotoUrlResult) {
        onAddPhotoSuccess(uploadedPhotoUrlResult)
      }

      setUploadImgProgress(false)
    },
    [uploadPhoto, onAddPhotoSuccess, textMap.imageProcessingError],
  )

  const handleProEditorCloseEditor = useCallback(() => {
    setIsEditProEditor(false)

    if (!uploadPhotoWarning && !uploadPhotoError) {
      handleCleanupPhoto()
    }

    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.grabMexProImageEditorClose,
      labels: {
        ...trackingLabels,
        HAS_WARNING: !!uploadPhotoWarning,
        HAS_ERROR: !!uploadPhotoError,
        HAS_BEEN_UPLOADED: hasBeenUploaded,
      },
    })
  }, [
    uploadPhotoWarning,
    uploadPhotoError,
    handleCleanupPhoto,
    eventCategory,
    trackingLabels,
    hasBeenUploaded,
  ])

  const handleProEditorRemoveBg = useCallback(
    (base64Image: string) =>
      handleEnhanceImage(base64Image, EnhanceImageTask.BACKGROUND_REMOVAL),
    [handleEnhanceImage],
  )

  const handleProEditorAutoRemoveBg = useCallback(
    (base64Image: string) =>
      handleEnhanceImage(base64Image, EnhanceImageTask.AUTO_BACKGROUND_REMOVAL),
    [handleEnhanceImage],
  )

  const handleProEditorError = useCallback(
    (errorMessage: string) => {
      // Disable pro editor and fall back to old cropper feature
      setProEditorHasError(true)
      setIsEditProEditor(false)

      datadogRum.addError(new Error(errorMessage), {
        tags: { context: "PhotoUploadDrawerProEditor" },
      })
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorError,
        labels: {
          ...trackingLabels,
          ERROR_MESSAGE: errorMessage,
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const handleProEditorImageEditingStarted = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.grabMexProImageEditorEditingStarted,
      labels: {
        ...trackingLabels,
      },
    })
  }, [eventCategory, trackingLabels])

  const handleProEditorAutoEnhanceStart = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.grabMexProImageEditorAutoEnhanceStart,
      labels: {
        ...trackingLabels,
      },
    })
  }, [eventCategory, trackingLabels])

  const handleProEditorAutoEnhanceEnd = useCallback(
    (result: AutoEnhanceResult) => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorAutoEnhanceEnd,
        labels: {
          ...trackingLabels,
          RESULT: result,
          IS_SUCCESS: result.isSuccess,
          DURATION: result.duration,
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const handleProEditorCropImage = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.grabMexProImageEditorCropImage,
      labels: {
        ...trackingLabels,
      },
    })
  }, [eventCategory, trackingLabels])

  const handleProEditorAutoAdjustEnd = useCallback(
    (adjustments: OptimalImageAdjustments) => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorAutoAdjustEnd,
        labels: {
          ...trackingLabels,
          RESULT: adjustments,
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const handleProEditorAdjustBrightnessManually = useCallback(
    (brightness: number) => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorAdjustBrightnessManually,
        labels: {
          ...trackingLabels,
          RESULT: { brightness },
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const handleProEditorAdjustContrastManually = useCallback(
    (contrast: number) => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorAdjustContrastManually,
        labels: {
          ...trackingLabels,
          RESULT: { contrast },
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const handleProEditorAutoRemoveBackgroundStart = useCallback(() => {
    TroyWebTracker.sendEvent({
      category: eventCategory,
      action: EventAction.grabMexProImageEditorAutoRemoveBackgroundStart,
      labels: {
        ...trackingLabels,
      },
    })
  }, [eventCategory, trackingLabels])

  const handleProEditorAutoRemoveBackgroundEnd = useCallback(
    (result: AutoEnhanceResult) => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorAutoRemoveBackgroundEnd,
        labels: {
          ...trackingLabels,
          RESULT: result,
          IS_SUCCESS: result.isSuccess,
          DURATION: result.duration,
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const handleProEditorSaveOrCancelWithData = useCallback(
    (data: SaveOrCancelData) => {
      TroyWebTracker.sendEvent({
        category: eventCategory,
        action: EventAction.grabMexProImageEditorSaveOrCancel,
        labels: {
          ...trackingLabels,
          // imageBase64 is excluded to avoid sending large base64 strings
          RESULT: omit(data, ["imageBase64"]),
          ACTION_TYPE: data.actionType,
        },
      })
    },
    [eventCategory, trackingLabels],
  )

  const proEditorCallbacks: ImageEditorCallbacks = {
    onImageEditingStarted: handleProEditorImageEditingStarted,
    onImageEditingComplete: handleProEditorImageEditingComplete,
    onCloseEditor: handleProEditorCloseEditor,
    onCallRemoveBgApi: handleProEditorRemoveBg,
    onCallAutoRemoveBgApi: handleProEditorAutoRemoveBg,
    onError: handleProEditorError,
    onAutoEnhanceStart: handleProEditorAutoEnhanceStart,
    onAutoEnhanceEnd: handleProEditorAutoEnhanceEnd,
    onCropImage: handleProEditorCropImage,
    onAutoAdjustEnd: handleProEditorAutoAdjustEnd,
    onAdjustBrightnessManually: handleProEditorAdjustBrightnessManually,
    onAdjustContrastManually: handleProEditorAdjustContrastManually,
    onAutoRemoveBackgroundStart: handleProEditorAutoRemoveBackgroundStart,
    onAutoRemoveBackgroundEnd: handleProEditorAutoRemoveBackgroundEnd,
    onSaveOrCancelWithData: handleProEditorSaveOrCancelWithData,
  }

  const proEditorTranslation: GrabMexI18nConfig = useMemo(
    () => ({
      autoEnhance: photoEnhancerTextMap.autoEnhancer,
      crop: photoEnhancerTextMap.crop,
      adjust: photoEnhancerTextMap.adjust,
      cleanUp: photoEnhancerTextMap.erase,
      optimizingImage: photoEnhancerTextMap.optimizingImage,
      beforeAndAfter: photoEnhancerTextMap.seeOriginal,
      autoEnhanceTitle: photoEnhancerTextMap.autoEnhancer,
      cancelText: photoEnhancerTextMap.cancel,
      mirrorImage: photoEnhancerTextMap.reverse,
      rotate: photoEnhancerTextMap.rotate,
      auto: photoEnhancerTextMap.autoEnhancer,
      brightness: photoEnhancerTextMap.brightness,
      contrast: photoEnhancerTextMap.contrast,
      cleanUpTitle: photoEnhancerTextMap.erase,
      removeBackground: photoEnhancerTextMap.removeBackground,
      removeBackgroundTitle: photoEnhancerTextMap.removeBackground,
      objectCleanup: photoEnhancerTextMap.removeObject,
      hangTightAiWorkingWonders: photoEnhancerTextMap.enhancementInProgress,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(photoEnhancerTextMap)],
  )

  return (
    <Drawer
      title={photoUploadTitle}
      width={md ? 480 : "100%"}
      css={photoUploadDrawer}
      open={visible}
      onClose={handleOnClosePhotoUploadDrawer}
      destroyOnClose
      // On mobile the drawer is full-screen, where a back-style chevron reads as
      // page navigation — use a close icon there; desktop keeps the chevron.
      closeIcon={md ? <ChevronLeftUniversal /> : <CloseUniversal />}
      afterOpenChange={nextVisible => !nextVisible && handleCleanupPhoto()}
      data-testid="photo-drawer"
    >
      <Flex
        vertical
        css={css`
          min-height: 100%;
        `}
      >
        <Row style={{ padding: "24px" }}>
          <Col span={24}>
            {readOnly && localUploadedImg && (
              <Flex justify="center">
                <img
                  src={localUploadedImg}
                  css={css`
                    width: 100%;
                  `}
                />
              </Flex>
            )}
            {!readOnly && shouldUseProEditor && isRtkFlagLoading && (
              <Flex justify="center">
                <Spin data-testid="rtk-flag-loading" />
              </Flex>
            )}
            {!readOnly && shouldUseProEditor && !isRtkFlagLoading && (
              <GrabMexProImageEditor
                inputImage={
                  isEditProEditor ? getBase64String(localUploadedImg) : ""
                }
                callbacks={proEditorCallbacks}
                translation={proEditorTranslation}
              />
            )}
            {!readOnly &&
              shouldUseProEditor &&
              !isEditProEditor &&
              localUploadedImg && (
                <Flex justify="center">
                  <img
                    src={localUploadedImg}
                    css={css`
                      width: 100%;
                    `}
                  />
                </Flex>
              )}
            {!readOnly && !localUploadedImg && isLoading && (
              <Flex
                data-testid="photo-upload-drawer-loading"
                justify="center"
                align="center"
                css={css`
                  height: 500px;
                `}
              >
                <Spin />
              </Flex>
            )}
            {!readOnly && !localUploadedImg && !isLoading && (
              <Dragger
                data-testid="photo-upload-drawer-dragger"
                name="file"
                showUploadList={false}
                onChange={onFileChange}
                beforeUpload={handleBeforeUploadPhoto}
                accept="image/png,image/jpg,image/jpeg"
                customRequest={() => {}}
              >
                <div>
                  <p className="dui-upload-drag-icon">
                    <img src={require("./uploadIcon.svg")} />
                  </p>
                  <p className="dui-upload-hint">{textMap.dragAndDropText}</p>
                  <Flex justify="center">
                    <Flex
                      justify="center"
                      align="center"
                      css={css`
                        background: #ffffff;
                        border: 1px solid #e8e8e8;
                        box-sizing: border-box;
                        border-radius: 8px;
                        width: 186px;
                        height: 54px;
                        font-size: 14px;
                        line-height: 21px;
                        color: #00a5cf;
                      `}
                    >
                      {textMap.uploadFromCompText}
                    </Flex>
                  </Flex>
                </div>
              </Dragger>
            )}
            {!readOnly && localUploadedImg && !shouldUseProEditor && (
              <div
                // cropper doesn't support attaching `data-testid`
                data-testid="photo-upload-drawer-cropper-container"
                css={css`
                  height: 426px;
                `}
              >
                <Cropper
                  image={localUploadedImg}
                  crop={crop}
                  rotation={rotation}
                  zoom={zoom}
                  aspect={1 / 1}
                  onCropChange={setCrop}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  onMediaLoaded={() => setUploadImgProgress(false)}
                  mediaProps={{ onError: handleCropperMediaError }}
                />
                {uploadImgProgress && (
                  <Flex
                    vertical
                    justify="center"
                    align="center"
                    data-testid="photo-spinner-upload"
                    css={css`
                      ${backdropFilter}
                      height: 100%;
                      width: 100%;
                    `}
                  >
                    <Spin />
                    <p>{textMap.uploading}</p>
                  </Flex>
                )}
              </div>
            )}
          </Col>
        </Row>
        {localUploadedImg && !uploadPhotoError && (
          <Row
            wrap={!md}
            css={css`
              padding: 0px 24px 24px 24px;
              gap: 10px;
            `}
          >
            {!shouldUseProEditor && (
              <Col flex={md ? 1 : "0 0 100%"}>
                <Button
                  onClick={handleRotatePhoto}
                  type="primary"
                  block
                  disabled={
                    (isEdit && !allowExistingPhotoEdit) ||
                    hasBeenUploaded ||
                    uploadImgProgress ||
                    readOnly
                  }
                  icon={<Rotate180Universal />}
                >
                  {textMap.rotatePhotoText}
                </Button>
              </Col>
            )}
            <Col flex={md ? 1 : "0 0 100%"}>
              <Button
                danger
                block
                onClick={handleCleanupPhoto}
                disabled={uploadImgProgress || readOnly}
              >
                {textMap.deletePhotoText}
              </Button>
            </Col>
            {!isWebview() && (isEdit || hasBeenUploaded) && (
              <Col flex={md ? 1 : "0 0 100%"}>
                <Button
                  data-testid="photo-btn-download"
                  onClick={handleDownloadPhoto}
                  disabled={uploadImgProgress}
                  block
                >
                  {textMap.downloadPhoto}
                </Button>
              </Col>
            )}
          </Row>
        )}
        {!uploadImgProgress && (uploadPhotoError || uploadPhotoWarning) && (
          <div
            data-testid="photo-issue-alert-container"
            css={css`
              padding: 0 24px 24px 24px;
            `}
          >
            <Row
              css={
                uploadPhotoError
                  ? uploadPhotoErrorStyle
                  : uploadPhotoWarningStyle
              }
            >
              <Col span={24}>
                <Alert
                  message={uploadPhotoError ?? uploadPhotoWarning}
                  type={uploadPhotoError ? "error" : "warning"}
                  showIcon
                />
              </Col>
            </Row>
            {uploadPhotoError && localUploadedImg && (
              <Row>
                <Button
                  data-testid="photo-btn-change-photo"
                  block
                  css={css`
                    font-weight: 500;
                  `}
                  onClick={handleCleanupPhoto}
                >
                  {textMap.selectAnotherPhoto}
                </Button>
              </Row>
            )}
          </div>
        )}
        <Row
          css={css`
            background: #f5f5f5;
            height: 63px;
            padding: 30px 12px 0px 24px;
            font-weight: 500;
            font-size: 16px;
            line-height: 19px;
            letter-spacing: 0.5px;
            text-transform: uppercase;
            color: #2d2d2d;
          `}
        >
          <Col span={24}>{textMap.photoGuideLineText}</Col>
        </Row>
        <Row
          css={css`
            padding: 24px;
            flex: 1 0;
          `}
        >
          <Col span={24}>
            <ul
              css={css`
                list-style: inside;
                li {
                  margin-bottom: 12px;
                }
              `}
            >
              <li>{textMap.upto2mb}</li>
              <li>{textMap.itemCenteredText}</li>
              <li>{textMap.blurryText}</li>
            </ul>
          </Col>
        </Row>
        {!shouldUseProEditor && (
          <span
            css={css`
              height: 75px;
              background-color: #f5f5f5;
            `}
          />
        )}
      </Flex>
      {(!shouldUseProEditor ||
        (uploadPhotoWarning && !validationHandlingOverride)) && (
        <div css={drawerFooter}>
          <Row>
            <Col span={24}>
              <Button
                data-testid="photo-btn-submit"
                block
                type="primary"
                onClick={onAddPhotoClick}
                disabled={
                  uploadImgProgress ||
                  !localUploadedImg ||
                  !!uploadPhotoError ||
                  (isEdit && !allowExistingPhotoEdit) ||
                  (!uploadFn && isRtkFlagLoading) ||
                  readOnly
                }
              >
                {uploadPhotoWarning && !validationHandlingOverride
                  ? // TODO GFMEX-8204 need translation (to be moved from Troy App)
                    "Confirm and upload"
                  : textMap.addPhotoText}
              </Button>
            </Col>
          </Row>
        </div>
      )}
    </Drawer>
  )
}
